import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { productionAPI, branchAPI } from '../api';

// Автоплан: «🤖 Предложить план» в таблице «Что запустить в работу».
//
// Алгоритм на сервере только предлагает. Здесь плановик видит черновик,
// правит пресс, смену и дату, снимает лишние строки и одной кнопкой отправляет
// всё в работу — через тот же approve, что и ручное согласование.

const BTN = {
  padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit',
};
const BTN_MAIN = { ...BTN, background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff' };
const INP = {
  padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--card)', color: 'var(--text)', fontSize: 12, fontFamily: 'inherit',
};
const TH = {
  padding: '7px 6px', fontSize: 11, fontWeight: 700, color: 'var(--muted)',
  textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};
const TD = { padding: '5px 6px', fontSize: 12, color: 'var(--text)', verticalAlign: 'top' };

// Цвета левой полосы групп: соседние карточки различаются с первого взгляда
const GROUP_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#a855f7'];

const MODES = [
  { v: 'off',  label: 'Выключен' },
  { v: 'semi', label: 'Полуавто' },
  { v: 'auto', label: 'Авто (этап 2, недоступно)', disabled: true },
];

const plural = (n) => {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'заказ';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'заказа';
  return 'заказов';
};

// «Ярлык: подробности» — ярлык жирным; «Перегрузка», «Срок: прошёл/не успевает» красным
function Reason({ r }) {
  const k = r.indexOf(': ');
  const label = k > 0 ? r.slice(0, k) : '';
  const rest = k > 0 ? r.slice(k + 2) : r;
  const bad = label === 'Перегрузка'
    || (label === 'Срок' && (/^прошёл/.test(rest) || rest.indexOf('не успевает') >= 0));
  return (
    <span style={bad ? { color: '#ef4444' } : undefined}>
      {label && <span style={{ fontWeight: 600 }}>{label}: </span>}{rest}
    </span>
  );
}

// Полоска метрик черновика; prev — метрики предыдущего черновика (для дельты)
const MetricsBar = ({ m, prev }) => {
  if (!m) return null;
  const delta = (cur, old) => {
    if (!prev || old == null || cur === old) return null;
    const up = cur > old;   // для всех этих метрик меньше — лучше
    return (
      <span style={{ marginLeft: 4, fontWeight: 600, color: up ? '#ef4444' : '#10b981' }}>
        {up ? '↑' : '↓'}{Math.abs(cur - old)}
      </span>
    );
  };
  const cell = (label, val, extra, d) => (
    <span style={{ whiteSpace: 'nowrap' }}>
      {label}: <b>{val}</b>{extra}{d}
    </span>
  );
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 16px',
      padding: '8px 12px', marginBottom: 10, borderRadius: 8, fontSize: 13,
      background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)',
    }}>
      {cell('Опоздают', `${m.late_orders} зак.`, ` (Σ ${m.late_days} дн.)`,
        delta(m.late_orders, prev && prev.late_orders))}
      {cell('Смен матрицы', m.changeovers, '', delta(m.changeovers, prev && prev.changeovers))}
      {cell('Перегруженных смен', m.overloaded_slots, '',
        delta(m.overloaded_slots, prev && prev.overloaded_slots))}
      {(m.by_press || []).length > 0 && (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          Загрузка:
          {m.by_press.map(p => (
            <span key={p.press_id} title={`${p.press_name}: ${p.sheets} л., смен матрицы ${p.changeovers}`} style={{
              padding: '1px 7px', borderRadius: 10, fontSize: 12,
              background: p.percent > 100 ? 'rgba(239,68,68,.18)' : 'rgba(16,185,129,.15)',
              color: p.percent > 100 ? '#ef4444' : 'var(--text)',
            }}>
              {p.press_name} {p.percent == null ? '—' : `${p.percent}%`}
            </span>
          ))}
        </span>
      )}
    </div>
  );
};

// Настройки алгоритма (PlannerConfig): подписи и допустимые границы
const CFG_NUM = [
  { k: 'freeze_shifts', label: 'Не трогать ближайшие смены', min: 0, max: 10,
    hint: 'Сколько ближайших смен план не меняет — новые позиции туда не ставятся (кроме просроченных)' },
  { k: 'max_overload_pct', label: 'Максимальная загрузка смены, %', min: 100, max: 300,
    hint: 'Больше этого процента от нормы смены план не загружает, пока есть другие варианты' },
  { k: 'late_penalty_per_day', label: 'Важность срока', min: 0, max: 1000,
    hint: 'Чем больше число, тем сильнее план держится за срок заказа — даже ценой лишней смены матрицы. Обычно 60' },
  { k: 'pack_window_days', label: 'Разница сроков в одной пачке, дней', min: 0, max: 30,
    hint: 'В одну пачку идут позиции, чьи сроки отличаются не больше чем на столько дней' },
  { k: 'search_time_ms', label: 'Время на улучшение плана, мс', min: 0, max: 10000,
    hint: 'Сколько времени план ищет вариант получше после первого расчёта; 0 — не искать' },
];
const CFG_BOOL = [
  { k: 'use_stock', label: 'Учитывать остатки плиты',
    hint: 'Не ставить в авто-план позиции, на которые не хватает материала' },
  { k: 'use_real_norm', label: 'Фактическая норма прессов',
    hint: 'Считать ёмкость по реальной скорости за последние недели, а не по норме из карточки' },
  { k: 'replan_waiting', label: 'Переносить ожидающие позиции очереди',
    hint: 'План может сдвинуть задания, по которым работа ещё не начата' },
];

const errText = (e, fallback) => e?.response?.data?.error || e?.response?.data?.detail || fallback;

export default function AutoPlanPanel({ onApplied }) {
  // Право планировать — то же, что видит остальное приложение (Login кладёт
  // can_plan в sessionStorage); директор и админ планируют без галочки.
  const role = sessionStorage.getItem('role');
  const canPlan = sessionStorage.getItem('can_plan') === '1'
    || ['admin', 'director'].includes(role);
  // «Все цеха» при нескольких цехах: цех для плана не выбран
  const [multiBranch, setMultiBranch] = useState(false);
  const noBranch = multiBranch
    && (sessionStorage.getItem('selected_branch') || 'all') === 'all';
  useEffect(() => {
    if (!canPlan) return;
    branchAPI.getList().then(r => setMultiBranch((r.data || []).length > 1))
      .catch(() => {});
  }, [canPlan]);

  // Настройки
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg]         = useState(null);   // {mode, join_max_percent, horizon_shifts}
  const [cfgSaving, setCfgSaving] = useState(false);

  // Предложенный план
  const [busy, setBusy]       = useState(false);
  const [plan, setPlan]       = useState(null);   // ответ propose
  const [lines, setLines]     = useState([]);     // правимые копии строк
  const [off, setOff]         = useState(() => new Set());   // исключённые order_item_id
  const [collapsed, setCollapsed] = useState(() => new Set());   // свёрнутые разделы прессов
  const [collapsedG, setCollapsedG] = useState(() => new Set());   // свёрнутые группы (пачки)
  const [stale, setStale]     = useState(() => new Set());   // устаревшие после 409

  const loadCfg = useCallback(() => {
    productionAPI.getPlannerConfig()
      .then(r => setCfg(r.data))
      .catch(() => {});
  }, []);
  // Режим нужен сразу, чтобы спрятать кнопку при «Выключен»
  useEffect(() => { if (canPlan && !noBranch && !cfg) loadCfg(); },
    [canPlan, noBranch, cfg, loadCfg]);

  const saveCfg = async () => {
    setCfgSaving(true);
    try {
      const { data } = await productionAPI.savePlannerConfig({
        mode: cfg.mode,
        join_max_percent: Number(cfg.join_max_percent) || 0,
        horizon_shifts: cfg.horizon_shifts,
        ...Object.fromEntries(CFG_NUM.map(f => [f.k, Number(cfg[f.k]) || 0])),
        ...Object.fromEntries(CFG_BOOL.map(f => [f.k, !!cfg[f.k]])),
      });
      setCfg(data);
      toast.success('Настройки автоплана сохранены');
    } catch (e) {
      toast.error(errText(e, 'Не удалось сохранить настройки'));
    } finally { setCfgSaving(false); }
  };

  const [prevMetrics, setPrevMetrics] = useState(null);   // метрики прошлого черновика в этой модалке

  const open = (data) => {
    setPrevMetrics(plan && plan.metrics ? plan.metrics : null);
    setPlan(data);
    setLines((data.lines || []).map(l => ({ ...l })));
    setOff(new Set());
    setStale(new Set());
  };

  const propose = async () => {
    setBusy(true);
    try {
      const { data } = await productionAPI.plannerPropose({});
      open(data);
    } catch (e) {
      if (e.response?.status === 400 && e.response?.data?.detail) {
        // Режим выключили, пока экран был открыт: подтянем настройки,
        // кнопка исчезнет сама
        toast.error(e.response.data.detail);
        loadCfg();
      } else {
        toast.error(errText(e, 'Не удалось построить план'));
      }
    } finally { setBusy(false); }
  };

  const dirty = lines.length > 0 && (off.size > 0 || lines.some((l, i) => {
    const o = (plan.lines || [])[i] || {};
    return String(l.press_id || '') !== String(o.press_id || '')
      || l.shift !== o.shift || (l.plan_date || '') !== (o.plan_date || '');
  }));

  const close = () => { setPrevMetrics(null); setCollapsed(new Set()); setCollapsedG(new Set()); setPlan(null); setLines([]); setOff(new Set()); setStale(new Set()); };

  const cancel = async () => {
    if (dirty && !window.confirm('Закрыть окно? Ваши правки плана пропадут.')) return;
    const id = plan && plan.id;
    close();
    if (id) productionAPI.plannerDiscard(id).catch(() => {});
  };

  // После 409: старый черновик отбрасываем и строим заново
  const recalc = async () => {
    const id = plan && plan.id;
    if (id) await productionAPI.plannerDiscard(id).catch(() => {});
    await propose();
  };

  const edit = (itemId, patch) =>
    setLines(ls => ls.map(l => (l.order_item_id === itemId ? { ...l, ...patch } : l)));

  const editGroup = (group, patch) =>
    setLines(ls => ls.map(l => (l.group === group ? { ...l, ...patch } : l)));

  const groupList = [];
  {
    const byGroup = new Map();
    lines.forEach(l => {
      if (!byGroup.has(l.group)) {
        const g = { group: l.group, items: [], real: (l.group_reasons || []).some(r => r.indexOf('Пачка: вливается') === 0) };
        byGroup.set(l.group, g);
        groupList.push(g);
      }
      byGroup.get(l.group).items.push(l);
    });
  }

  const pressSections = [];
  {
    const slotRows = (plan && plan.press_load) || [];
    const today = slotRows.reduce((m, r) => (!m || r.date < m ? r.date : m), '');
    const slotInfo = new Map(slotRows.map(r => [r.press_id + '|' + r.date + '|' + r.shift, r]));
    const pressName = new Map(((plan && plan.presses) || []).map(p => [String(p.id), p.name]));
    const order = ((plan && plan.presses) || []).map(p => String(p.id));
    const secMap = new Map();
    groupList.forEach(g => {
      const h = g.items[0];
      const pid = h.press_id ? String(h.press_id) : '';
      if (!secMap.has(pid)) {
        secMap.set(pid, { key: pid || 'none', pid, name: pid ? (pressName.get(pid) || ('Пресс ' + pid)) : 'Без пресса', groups: [] });
      }
      secMap.get(pid).groups.push(g);
    });
    const keys = [...secMap.keys()].sort((x, y) => {
      const ix = order.indexOf(x), iy = order.indexOf(y);
      return (ix < 0 ? 999 : ix) - (iy < 0 ? 999 : iy);
    });
    keys.forEach(k => {
      const sec = secMap.get(k);
      const slotSum = new Map();
      let total = 0, orders = 0;
      sec.groups.forEach(g => {
        const h = g.items[0];
        const live = g.items.filter(l => !off.has(l.order_item_id));
        const q = live.reduce((x, l) => x + (Number(l.quantity) || 0), 0);
        total += q; orders += live.length;
        const sk = (h.plan_date || today) + '|' + (h.shift || 'day');
        slotSum.set(sk, (slotSum.get(sk) || 0) + q);
      });
      sec.total = total; sec.orders = orders;
      sec.loads = [...slotSum.keys()].sort().map(sk => {
        const [date, shift] = sk.split('|');
        const info = slotInfo.get(sec.pid + '|' + date + '|' + shift);
        const load = (info ? info.base : 0) + slotSum.get(sk);
        const cap = info ? info.cap : null;
        const dd = date.slice(8, 10) + '.' + date.slice(5, 7);
        return { text: (shift === 'night' ? 'ночь ' : 'день ') + dd + ' — ' + load + (cap ? '/' + cap : ' л.'), over: !!cap && load > cap };
      });
      pressSections.push(sec);
    });
  }

  const toggleSection = (key) => setCollapsed(c => {
    const n = new Set(c);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const toggleGroup = (key) => setCollapsedG(c => {
    const n = new Set(c);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const toggle = (itemId) => setOff(s => {
    const n = new Set(s);
    if (n.has(itemId)) n.delete(itemId); else n.add(itemId);
    return n;
  });

  const chosen = lines.filter(l => !off.has(l.order_item_id));
  const noPress = chosen.filter(l => !l.press_id);
  // После 409 отправлять нельзя, пока в выборе есть устаревшие строки:
  // снимите их галочкой или нажмите «Пересчитать»
  const staleChosen = chosen.filter(l => stale.has(l.order_item_id));

  const apply = async () => {
    if (chosen.length === 0 || noPress.length > 0 || staleChosen.length > 0) return;
    setBusy(true);
    try {
      const { data } = await productionAPI.plannerApply(plan.id, {
        lines: chosen.map(l => ({
          order_item_id: l.order_item_id,
          press_id: Number(l.press_id),
          shift: l.shift,
          plan_date: l.plan_date,
        })),
      });
      toast.success(`✅ Отправлено в работу: ${data.applied} поз.`);
      close();
      onApplied && onApplied();
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 409 && d && Array.isArray(d.stale)) {
        setStale(new Set(d.stale));
        toast.error('Часть позиций уже назначена — план устарел. Пересчитайте.');
      } else {
        toast.error(errText(e, 'Не удалось отправить в работу'));
      }
    } finally { setBusy(false); }
  };

  const modeOff = !!cfg && cfg.mode === 'off';
  const canPropose = !modeOff && !noBranch;
  const presses = (plan && plan.presses) || [];
  const skipped = (plan && plan.skipped) || [];

  if (!canPlan) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {canPropose && (
          <button onClick={propose} disabled={busy} style={BTN_MAIN}>
            {busy && !plan ? 'Считаю…' : '🤖 Предложить план'}
          </button>
        )}
        <button onClick={() => setCfgOpen(o => !o)} style={BTN}>
          ⚙ Автоплан {cfgOpen ? '▴' : '▾'}
        </button>
        {noBranch && (
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            Выберите цех в шапке, чтобы построить план
          </span>
        )}
      </div>

      {cfgOpen && (
        <div style={{
          marginTop: 8, padding: 12, background: 'var(--bg)', borderRadius: 10,
          border: '1px solid var(--border)', display: 'flex', gap: 16,
          alignItems: 'flex-end', flexWrap: 'wrap',
        }}>
          {!cfg ? (
            <span style={{ color: 'var(--mid)', fontSize: 13 }}>Загрузка…</span>
          ) : (
            <>
              <label style={{ fontSize: 12, color: 'var(--muted)' }}>
                Режим
                <select value={cfg.mode} style={{ ...INP, display: 'block', marginTop: 3 }}
                  onChange={e => setCfg({ ...cfg, mode: e.target.value })}>
                  {MODES.map(m => (
                    <option key={m.v} value={m.v} disabled={m.disabled && cfg.mode !== m.v}>{m.label}</option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 12, color: 'var(--muted)' }}>
                Добавлять в начатую пачку, пока сделано не больше, %
                <input type="number" min="0" max="100" value={cfg.join_max_percent}
                  style={{ ...INP, display: 'block', marginTop: 3, width: 90 }}
                  onChange={e => setCfg({ ...cfg, join_max_percent: e.target.value })} />
              </label>
              {CFG_NUM.map(f => (
                <label key={f.k} style={{ fontSize: 12, color: 'var(--muted)' }} title={f.hint}>
                  {f.label}
                  <input type="number" min={f.min} max={f.max} value={cfg[f.k] ?? ''}
                    style={{ ...INP, display: 'block', marginTop: 3, width: 110 }}
                    onChange={e => setCfg({ ...cfg, [f.k]: e.target.value })} />
                </label>
              ))}
              {CFG_BOOL.map(f => (
                <label key={f.k} style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }} title={f.hint}>
                  <input type="checkbox" checked={!!cfg[f.k]}
                    onChange={e => setCfg({ ...cfg, [f.k]: e.target.checked })} />
                  {f.label}
                </label>
              ))}
              <button onClick={saveCfg} disabled={cfgSaving} style={BTN}>
                {cfgSaving ? 'Сохранение…' : 'Сохранить'}
              </button>
              <span style={{ fontSize: 11, color: 'var(--dim)', flexBasis: '100%' }}>
                Полуавто: план строится по кнопке, в работу уходит после вашего подтверждения.
                Автоматический режим появится на следующем этапе.
              </span>
            </>
          )}
        </div>
      )}

      {plan && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,.6)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: 16, overflowY: 'auto',
        }}>
          <div style={{
            background: 'var(--bg)', borderRadius: 14, padding: 16,
            maxWidth: 1200, width: '100%', marginTop: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <b style={{ color: 'var(--text)', fontSize: 17, flex: 1 }}>🤖 Предложенный план</b>
              {lines.length > 0 && (
                <>
                  <button onClick={() => setCollapsedG(new Set(groupList.map(g => g.group)))} style={BTN}>Свернуть все</button>
                  <button onClick={() => setCollapsedG(new Set())} style={BTN}>Развернуть все</button>
                </>
              )}
              <button onClick={cancel} style={{
                background: 'none', border: 'none', color: 'var(--dim)',
                fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            <MetricsBar m={plan.metrics} prev={prevMetrics} />

            {stale.size > 0 && (
              <div style={{
                padding: '8px 12px', marginBottom: 10, borderRadius: 8, fontSize: 13,
                background: 'rgba(239,68,68,.15)', color: '#ef4444',
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                <span style={{ flex: 1 }}>
                  Часть позиций (выделены красным) уже назначена кем-то другим или заказ изменился. Ничего не применено: снимите их галочкой или пересчитайте.
                </span>
                <button onClick={recalc} disabled={busy} style={BTN_MAIN}>Пересчитать</button>
              </div>
            )}

            {lines.length === 0 ? (
              <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 16 }}>
                Нечего планировать: подходящих позиций нет.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pressSections.map(sec => (
                  <div key={sec.key} style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                    <div onClick={() => toggleSection(sec.key)} style={{
                      padding: '9px 12px', cursor: 'pointer', background: 'rgba(128,128,128,.12)',
                    }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <b style={{ color: 'var(--text)', fontSize: 14 }}>
                          {collapsed.has(sec.key) ? '▸' : '▾'} {sec.name}
                        </b>
                        <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>
                          {sec.total} л., {sec.groups.length} пачек, {sec.orders} {plural(sec.orders)}
                        </span>
                      </div>
                      {sec.loads.length > 0 && (
                        <div style={{ fontSize: 11, marginTop: 3, color: 'var(--muted)' }}>
                          <span style={{ fontWeight: 600 }}>Загрузка: </span>
                          {sec.loads.map((x, i) => (
                            <span key={i} style={x.over ? { color: '#ef4444' } : undefined}>
                              {i > 0 ? ', ' : ''}{x.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {!collapsed.has(sec.key) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 8 }}>
                {sec.groups.map((g, gi) => {
                  const h = g.items[0];
                  const allOff = g.items.every(l => off.has(l.order_item_id));
                  const total = g.items.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
                  const sameTex = h.texture_name && h.texture_name !== '—';
                  const gClosed = collapsedG.has(g.group);
                  const marked = g.items.filter(l => !off.has(l.order_item_id)).length;
                  const stripe = GROUP_COLORS[gi % GROUP_COLORS.length];
                  const dd = h.plan_date ? h.plan_date.slice(8, 10) + '.' + h.plan_date.slice(5, 7) : '—';
                  const pName = h.press_id
                    ? (presses.find(p => String(p.id) === String(h.press_id)) || {}).name || ('Пресс ' + h.press_id)
                    : 'без пресса';
                  return (
                    <div key={g.group} style={{
                      border: '2px solid var(--border)', borderLeft: '4px solid ' + stripe,
                      borderRadius: 10, overflow: 'hidden',
                      background: gi % 2 === 0 ? 'rgba(128,128,128,.05)' : 'rgba(128,128,128,.12)',
                    }}>
                      <div onClick={() => toggleGroup(g.group)} style={{
                        padding: '8px 12px', background: 'rgba(128,128,128,.2)',
                        cursor: 'pointer',
                        opacity: allOff ? 0.4 : 1,
                      }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <b style={{ color: 'var(--text)', fontSize: 13, overflowWrap: 'anywhere' }}>
                            {gClosed ? '▸' : '▾'} {h.product_name}{sameTex ? ' · ' + h.texture_name : ''}
                          </b>
                          <b style={{ color: 'var(--text)', fontSize: 13 }}>
                            {total} л., {g.items.length} {plural(g.items.length)}
                          </b>
                          {(g.real || g.items.length > 1) && (
                            <span style={{
                              padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                              background: 'rgba(34,197,94,.18)', color: '#22c55e',
                            }}>{g.real ? 'вливается в начатую' : 'в пачку'}</span>
                          )}
                        </div>
                        {gClosed && (
                          <div style={{ fontSize: 11, marginTop: 3, color: 'var(--muted)' }}>
                            {pName} · {h.shift === 'night' ? 'ночь' : 'день'} · {dd} · {marked}/{g.items.length} отмечено
                          </div>
                        )}
                        <div onClick={e => e.stopPropagation()}
                          style={{ display: gClosed ? 'none' : 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                          <select value={h.press_id || ''} disabled={allOff} style={INP}
                            onChange={e => editGroup(g.group, { press_id: e.target.value })}>
                            <option value="">— пресс —</option>
                            {presses.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                          <select value={h.shift || 'day'} disabled={allOff} style={INP}
                            onChange={e => editGroup(g.group, { shift: e.target.value })}>
                            <option value="day">День</option>
                            <option value="night">Ночь</option>
                          </select>
                          <input type="date" value={h.plan_date || ''} disabled={allOff} style={INP}
                            onChange={e => editGroup(g.group, { plan_date: e.target.value })} />
                        </div>
                        {!gClosed && (h.group_reasons || []).length > 0 && (
                          <ul style={{ margin: '6px 0 0', paddingLeft: 16, color: 'var(--muted)', fontSize: 11 }}>
                            {h.group_reasons.map((r, i) => <li key={i}><Reason r={r} /></li>)}
                          </ul>
                        )}
                      </div>
                      {!gClosed && (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {g.items.map(l => {
                            const excluded = off.has(l.order_item_id);
                            const isStale = stale.has(l.order_item_id);
                            return (
                              <tr key={l.order_item_id} style={{
                                opacity: excluded ? 0.4 : 1,
                                background: isStale ? 'rgba(239,68,68,.15)'
                                  : l.warning ? 'rgba(245,158,11,.12)' : 'transparent',
                                borderTop: '1px solid var(--border)',
                              }}>
                                <td style={{ ...TD, width: 28 }}>
                                  <input type="checkbox" checked={!excluded}
                                    onChange={() => toggle(l.order_item_id)} />
                                </td>
                                <td style={TD}>{l.order_number}</td>
                                <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{l.quantity}</td>
                                <td style={{ ...TD, minWidth: 220 }}>
                                  {l.warning && (
                                    <div style={{ color: '#f59e0b', fontSize: 12, fontWeight: 600 }}>
                                      ⚠ {l.warning}
                                    </div>
                                  )}
                                  {(l.reasons || []).length > 0 && (
                                    <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--muted)', fontSize: 11 }}>
                                      {l.reasons.map((r, i) => <li key={i}><Reason r={r} /></li>)}
                                    </ul>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      )}
                    </div>
                  );
                })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {skipped.length > 0 && (
              <div style={{
                marginTop: 12, padding: '8px 12px', borderRadius: 8,
                border: '1px solid var(--border)',
              }}>
                <b style={{ fontSize: 13, color: 'var(--text)' }}>Пропущено ({skipped.length})</b>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--muted)' }}>
                  {skipped.map(s => (
                    <li key={s.order_item_id}>
                      {s.order_number} · {s.product_name} — {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--muted)', fontSize: 13 }}>
                Выбрано: <b style={{ color: 'var(--text)' }}>{chosen.length}</b> из {lines.length}
                {noPress.length > 0 && (
                  <span style={{ color: '#ef4444' }}> · без пресса: {noPress.length}</span>
                )}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                <button onClick={cancel} style={BTN}>Отмена</button>
                <button onClick={apply}
                  disabled={busy || chosen.length === 0 || noPress.length > 0 || staleChosen.length > 0}
                  style={{ ...BTN_MAIN, opacity: (busy || chosen.length === 0 || noPress.length > 0 || staleChosen.length > 0) ? 0.5 : 1 }}>
                  {busy ? 'Отправка…' : 'Отправить в работу'}
                </button>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
